import Spinner from 'components/common/spinner';
import ViolationsTable from 'components/violations/ViolationTable';
import useGetOffences from 'hooks/offences/useGetOffences';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Offence } from 'utils/types';
import { Plus, X } from 'lucide-react';
import { Bounce, toast } from 'react-toastify';
import useAddOffence from 'hooks/offences/useAddnewOffence';

interface AddOffenceForm {
  name: string;
  isSlabRateApplicable: '' | 'Y' | 'N';
  actSection: string;
  offenceLabel: string;
  amount: number;
}

const initialForm: AddOffenceForm = {
  name: '',
  isSlabRateApplicable: '',
  offenceLabel: '',
  actSection: '',
  amount: 0,
};

const Violations = () => {
  const navigate = useNavigate();
  const { data: offences, isLoading: offencesLoading } = useGetOffences();
  const { mutateAsync: addOffence, isPending } = useAddOffence();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = 10;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState<AddOffenceForm>(initialForm);

  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get('page') || '1', 10),
  );

  useEffect(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    setCurrentPage(page);
  }, [searchParams]);

  const goToPage = (pageNumber: number) => {
    if (offences !== undefined && pageNumber > 0) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', pageNumber.toString());
      setSearchParams(newSearchParams);
    }
  };

  const handleRowClick = (offence: Offence) => {
    const currentIndex = offences?.findIndex((f) => f.id === offence.id);
    navigate(`/violations/single/${offence.id}`, {
      state: { offence, currentIndex },
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await addOffence(form)
      .then(() => {
        toast.success('Violation added successfully', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
        setForm(initialForm);
        setIsModalOpen(false);
      })
      .catch(() => {
        toast.error('There was an error adding the violation', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
      });
  };

  if (offencesLoading) return <Spinner />;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Add Violation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={!isPending ? () => setIsModalOpen(false) : undefined}
          />
          <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-md flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                Add New Violation
              </h2>
              <button
                onClick={() => setIsModalOpen(false)}
                disabled={isPending}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Offence Name
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter offence name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Offence Label
                </label>
                <input
                  name="offenceLabel"
                  value={form.offenceLabel}
                  onChange={handleChange}
                  required
                  placeholder="Enter offence label"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section Act
                </label>
                <input
                  name="actSection"
                  value={form.actSection}
                  onChange={handleChange}
                  required
                  placeholder="Enter Section Act"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (KES)
                </label>
                <input
                  name="amount"
                  type="number"
                  min={0}
                  value={form.amount}
                  onChange={handleChange}
                  required
                  placeholder="Enter amount"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slab Rate Applicable
                </label>
                <select
                  name="isSlabRateApplicable"
                  value={form.isSlabRateApplicable}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="" disabled>
                    Select option
                  </option>
                  <option value="Y">Yes</option>
                  <option value="N">No</option>
                </select>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  disabled={isPending}
                  className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isPending}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPending ? 'Saving...' : 'Save Violation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table header with Add button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setIsModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors active:scale-95"
        >
          <Plus size={16} />
          Add Violation
        </button>
      </div>

      <ViolationsTable
        data={offences ?? []}
        totalPages={3}
        goToPage={goToPage}
        currentPage={currentPage}
        pageSize={pageSize}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default Violations;
